import { useEffect, useMemo, useState } from 'react';

import styled from 'styled-components';

import {
    AuditLogCreatedSubscription,
    AuditLogCreatedSubscriptionVariables,
    AuditLogEntryFragmentFragment,
    AuditOperation,
    GetAuditLogsQuery,
    useGetAuditLogsQuery,
} from 'data/__generated__';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { AUDIT_LOG_CREATED_SUBSCRIPTION } from 'data/subscriptions/auditLog';

import { Loader } from 'components/Loader';

import { AuditChange, AuditChangeList } from './AuditChangeList';

/** `documentId` → ce qui l'identifie pour un humain, ex. `{ 'loc-1': '(1,2)' }`. Fourni par la page. */
export type SubjectLabels = Record<string, string>;

/**
 * Le plafond de la section, côté serveur comme côté cache.
 *
 * Explicite, parce qu'un défaut qui ne vit que dans le repository est une garantie qu'on ne peut pas
 * lire d'ici — et parce que la souscription, elle, empile dans le MÊME tableau : sans un nombre à
 * comparer, une section laissée ouverte sur un document actif dépasserait le plafond sans que rien
 * ne le dise.
 */
export const AUDIT_LOGS_LIMIT = 100;

/**
 * Au-delà, un groupe est tronqué à l'ouverture.
 *
 * Un enregistrement de LOT — un planning de livraison, des centaines de documents en un clic — ouvre
 * sinon autant de blocs de changements qu'il a écrit de lignes, chacun avec ses valeurs AVANT/APRÈS
 * sérialisées. Le compte exact reste affiché dans l'en-tête : c'est le RENDU qu'on borne, pas la
 * mesure.
 */
const MAX_RENDERED_CHANGES = 100;

/** Au-delà, l'en-tête d'une action ne nomme plus ses sujets un par un. */
const MAX_HEADER_SUBJECTS = 5;

type AuditLogsProps = {
    /**
     * Le document regardé, ET les objets que la page considère comme siens — une page colonne passe
     * aussi ses étagères et ses emplacements. Le serveur ne connaît aucune hiérarchie : c'est
     * l'appelant qui sait ce qu'il affiche.
     */
    collectionDocumentIds?: string[];
    /**
     * Les collections regardées, pour une page qui n'a PAS de document : le planning de livraison est
     * une grille de centaines de documents dont aucun n'est le sujet. Son sujet, c'est la collection.
     */
    collectionNames?: string[];
    /** De quoi nommer un document secondaire autrement que par son identifiant. */
    subjectLabels?: SubjectLabels;
};

/** Une écriture, sur le document regardé ou sur un autre. */
type AuditLogEntry = AuditLogEntryFragmentFragment;

/** Une ligne telle que la requête la rend : l'entrée, plus ses voisines quand on les a demandées. */
type AuditLogRow = GetAuditLogsQuery['auditLogs'][number];

/**
 * Une ACTION : une écriture sur le document regardé, et tout ce que la MÊME requête a écrit ailleurs.
 *
 * Rien n'est recousu ici : le serveur porte les écritures voisines sur chaque ligne
 * (`sameRequestAuditLogs`). Les lignes antérieures à `requestId` n'en ont pas — chacune forme alors
 * son propre groupe, ce qui rend exactement l'affichage d'avant.
 */
type AuditAction = {
    key: string;
    createdAt: number;
    createdBy: string;
    createdByName: string;
    requestName: string | null;
    /** Le document principal d'abord — c'est lui qu'on regardait — puis les documents secondaires. */
    logs: AuditLogEntry[];
};

export const OPERATION_LABELS: Record<AuditOperation, string> = {
    [AuditOperation.Create]: 'Création',
    [AuditOperation.Update]: 'Modification',
    [AuditOperation.Delete]: 'Suppression',
};

export const getCreatorName = (createdBy: string, createdByName: string) => createdByName || createdBy || 'Inconnu';

/**
 * « Emplacement (2,3) » plutôt que « Emplacement ».
 *
 * L'identité d'un document n'est PAS calculée au moment de l'audit : rien de métier n'a sa place dans
 * ce processus, et une capture à l'écriture serait de toute façon incomplète. C'est la page qui la
 * fournit — elle a déjà les objets sous les yeux.
 */
export const getSubjectLabel = (
    collectionName: string,
    collectionDocumentId: string,
    subjectLabels?: SubjectLabels,
): string => {
    const subject = subjectLabels?.[collectionDocumentId];

    return subject ? `${collectionName} ${subject}` : collectionName;
};

/** `UPDATE_PRODUCT` → `Update product`. Le nom reste technique, mais il situe l'action. */
export const formatRequestName = (requestName: string | null | undefined): string | null => {
    if (!requestName) {
        return null;
    }

    const words = requestName
        .replace(/Mutation$/, '')
        .replace(/_/g, ' ')
        .toLowerCase();

    return words.charAt(0).toUpperCase() + words.slice(1);
};

const groupByAction = (auditLogs: readonly AuditLogRow[]): AuditAction[] => {
    const actions = new Map<string, { action: AuditAction; logsById: Map<string, AuditLogEntry> }>();

    auditLogs.forEach((auditLog) => {
        // Sans `requestId`, la ligne est sa propre action : son `_id` est une clé nécessairement unique.
        const key = auditLog.requestId ?? auditLog._id;
        const entry = actions.get(key) ?? {
            action: {
                key,
                createdAt: auditLog.createdAt,
                createdBy: auditLog.createdBy,
                createdByName: auditLog.createdByName,
                requestName: auditLog.requestName ?? null,
                logs: [],
            },
            // Deux écritures du même clic sur des documents affichés portent LES MÊMES voisines : la
            // table par `_id` les dédoublonne, et son ordre d'insertion garde les principales en tête.
            logsById: new Map<string, AuditLogEntry>(),
        };

        // `sameRequestAuditLogs` est absent en mode COLLECTION : les voisines sont déjà dans la liste,
        // et la clé `requestId` les rassemble dans ce même groupe au fil des itérations.
        [auditLog, ...(auditLog.sameRequestAuditLogs ?? [])].forEach((log) => entry.logsById.set(log._id, log));
        // Une action est datée par sa dernière écriture.
        entry.action.createdAt = Math.max(entry.action.createdAt, auditLog.createdAt);
        actions.set(key, entry);
    });

    return [...actions.values()]
        .map(({ action, logsById }) => ({ ...action, logs: [...logsById.values()] }))
        .sort((a, b) => b.createdAt - a.createdAt);
};

export const AuditLogs = ({ collectionDocumentIds, collectionNames, subjectLabels }: AuditLogsProps) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
    // Le document PRINCIPAL de la page — le premier de la liste. Sert au rendu, pour ne nommer la
    // collection que sur les lignes venues d'un objet lié. Nul en mode collection : là, TOUTE ligne
    // vient d'un document différent, donc toutes méritent d'être nommées.
    const [subjectId] = collectionDocumentIds ?? [];
    /**
     * Les voisines d'une action ne se demandent QUE lorsqu'on regarde des documents nommés — elles
     * sont alors le seul moyen de voir ce que la même action a écrit ailleurs. En mode collection
     * elles sont déjà dans la liste, et les redemander sur chaque ligne multiplierait la charge utile
     * par la taille d'un enregistrement de lot.
     */
    const withSameRequestAuditLogs = !collectionNames?.length;
    /**
     * Quand la page est scopée sur UNE collection, son nom ne sert plus à rien sur les sujets : tout
     * vient d'elle. Il ne situe que ce qui arrive d'AILLEURS — et là, il n'y a pas d'ailleurs.
     */
    const scopedCollectionName = collectionNames?.length === 1 ? collectionNames[0] : undefined;
    const subjectLabelFor = ({
        collectionName,
        collectionDocumentId,
    }: Pick<AuditLogEntry, 'collectionName' | 'collectionDocumentId'>): string =>
        collectionName === scopedCollectionName
            ? (subjectLabels?.[collectionDocumentId] ?? collectionName)
            : getSubjectLabel(collectionName, collectionDocumentId, subjectLabels);
    const { data, loading, error, subscribeToMore } = useGetAuditLogsQuery({
        variables: {
            collectionDocumentIds,
            collectionNames,
            limit: AUDIT_LOGS_LIMIT,
            withSameRequestAuditLogs,
        },
    });

    /**
     * La souscription dépend du CONTENU des listes, pas de leur référence : plusieurs appelants
     * passent un tableau littéral (`[siteId]`, `[organizationId]`), donc une nouvelle référence à
     * chaque rendu — s'en servir comme dépendance relancerait la souscription en boucle. Les clés
     * sont redécoupées dans l'effet pour que la dépendance reste exacte, sans échappatoire ESLint.
     */
    const documentsKey = (collectionDocumentIds ?? []).join('|');
    const collectionsKey = (collectionNames ?? []).join('|');

    useEffect(() => {
        // La souscription porte sur le document ET ses objets : au singulier, changer le code MDB d'un
        // emplacement n'arrivait jamais en direct. Le filtre vit côté serveur.
        const subscribedIds = documentsKey ? documentsKey.split('|') : [];
        const subscribedCollections = collectionsKey ? collectionsKey.split('|') : [];

        const unsubscribe = subscribeToMore<AuditLogCreatedSubscription, AuditLogCreatedSubscriptionVariables>({
            variables: {
                collectionDocumentIds: subscribedIds,
                collectionNames: subscribedCollections,
                withSameRequestAuditLogs: !subscribedCollections.length,
            },
            document: AUDIT_LOG_CREATED_SUBSCRIPTION,
            updateQuery: (prevData, { subscriptionData }) => {
                const createdAuditLog = subscriptionData.data?.auditLogCreated;

                if (!createdAuditLog) {
                    return prevData;
                }

                // Dédoublonnage : la même ligne peut arriver par la souscription ET par un refetch.
                if (prevData.auditLogs.some(({ _id }) => _id === createdAuditLog._id)) {
                    return prevData;
                }

                // Tronqué au plafond : la requête, elle, en rend au plus `AUDIT_LOGS_LIMIT`, mais rien
                // n'élaguait ce que la souscription empile — une section laissée ouverte sur un
                // document actif grossissait sans fin.
                return { auditLogs: [createdAuditLog, ...prevData.auditLogs].slice(0, AUDIT_LOGS_LIMIT) };
            },
        });

        return () => unsubscribe();
        // Les identifiants manquaient dans les deps : la souscription restait accrochée au premier
        // document affiché quand la page passait de l'un à l'autre sans se démonter.
    }, [subscribeToMore, documentsKey, collectionsKey]);

    const actions = useMemo(() => groupByAction(data?.auditLogs ?? []), [data?.auditLogs]);

    const toggleExpanded = (key: string) =>
        setExpandedKeys((previous) => {
            const next = new Set(previous);

            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }

            return next;
        });

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <Message $isError>Erreur lors du chargement de l’historique : {error.message}</Message>;
    }

    if (!actions.length) {
        return (
            <Container>
                <Message>Aucune modification enregistrée</Message>
            </Container>
        );
    }

    return (
        <Container>
            <ActionList>
                {actions.map((action) => {
                    const isExpanded = expandedKeys.has(action.key);
                    const changes: AuditChange[] = action.logs.flatMap((log) =>
                        log.patches.map((patch, index) => ({
                            key: `${log._id}-${index}`,
                            collectionName: log.collectionName,
                            // Le nom de la collection ne se répète pas quand tout vient du document
                            // affiché : il ne sert qu'à situer un changement venu d'un objet lié.
                            collectionLabel: log.collectionDocumentId === subjectId ? undefined : subjectLabelFor(log),
                            patch,
                        })),
                    );
                    const operations = [...new Set(action.logs.map(({ operation }) => operation))];
                    const isExpandable = changes.length > 0;
                    /**
                     * Les documents que l'action a touchés SANS produire de changement lisible.
                     *
                     * Une CRÉATION ne porte aucun patch — délibéré : ils recopieraient un document que
                     * la base contient toujours. Sans ce rappel, elle s'affiche « Création » et rien
                     * d'autre : ni quoi, ni où. C'est le seul endroit où son sujet peut apparaître.
                     */
                    const silentSubjects = [
                        ...new Set(action.logs.filter(({ patches }) => !patches.length).map(subjectLabelFor)),
                    ];
                    const hiddenSubjectsCount = silentSubjects.length - MAX_HEADER_SUBJECTS;
                    // Le compte AFFICHÉ reste celui de l'action entière ; seul le rendu est borné.
                    const hiddenChangesCount = changes.length - MAX_RENDERED_CHANGES;

                    return (
                        <ActionItem key={action.key}>
                            <ActionHeader
                                $isExpandable={isExpandable}
                                onClick={() => isExpandable && toggleExpanded(action.key)}
                            >
                                <ActionInfo>
                                    <ActionDate>
                                        {getExtendedFormattedDatetime(action.createdAt)} par{' '}
                                        {getCreatorName(action.createdBy, action.createdByName)}
                                    </ActionDate>
                                    <ActionSummary>
                                        {operations.map((operation) => OPERATION_LABELS[operation]).join(' · ')}
                                        {formatRequestName(action.requestName)
                                            ? ` · ${formatRequestName(action.requestName)}`
                                            : ''}
                                        {isExpandable
                                            ? ` · ${changes.length} changement${changes.length > 1 ? 's' : ''}`
                                            : ''}
                                    </ActionSummary>
                                    {silentSubjects.length ? (
                                        <ActionSubjects>
                                            {silentSubjects.slice(0, MAX_HEADER_SUBJECTS).join(' · ')}
                                            {hiddenSubjectsCount > 0 ? ` · et ${hiddenSubjectsCount} autre` : ''}
                                            {hiddenSubjectsCount > 1 ? 's' : ''}
                                        </ActionSubjects>
                                    ) : null}
                                </ActionInfo>
                                {isExpandable ? <ExpandIcon $isExpanded={isExpanded}>▼</ExpandIcon> : null}
                            </ActionHeader>

                            {isExpanded ? (
                                <>
                                    <AuditChangeList changes={changes.slice(0, MAX_RENDERED_CHANGES)} />
                                    {hiddenChangesCount > 0 ? (
                                        <Message>
                                            et {hiddenChangesCount} autre{hiddenChangesCount > 1 ? 's' : ''} changement
                                            {hiddenChangesCount > 1 ? 's' : ''} — voir tout l’historique
                                        </Message>
                                    ) : null}
                                </>
                            ) : null}
                        </ActionItem>
                    );
                })}
            </ActionList>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const ActionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 520px;
    overflow-y: auto;
`;

const ActionItem = styled.div`
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 6px;
    padding: 6px 12px;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ActionHeader = styled.div<{ $isExpandable: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
    padding-bottom: 4px;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    cursor: ${({ $isExpandable }) => ($isExpandable ? 'pointer' : 'default')};
`;

const ActionInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ActionDate = styled.span`
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    font-size: 14px;
`;

const ActionSummary = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
`;

const ActionSubjects = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.textColor};
    opacity: 0.75;
`;

const ExpandIcon = styled.span<{ $isExpanded: boolean }>`
    font-size: 12px;
    color: ${({ theme }) => theme.ctaPrimaryTextColor};
    transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.2s ease;
    user-select: none;
`;

const Message = styled.div<{ $isError?: boolean }>`
    color: ${({ theme, $isError }) => ($isError ? theme.errorColor : theme.textColor)};
    opacity: ${({ $isError }) => ($isError ? 1 : 0.6)};
    font-size: 13px;
    padding: 8px 0;
`;
